import {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react"
import { DndContext, rectIntersection } from "@dnd-kit/core"
import { restrictToWindowEdges } from "@dnd-kit/modifiers"
import moment from "moment"
import { useNavigate, useParams } from "react-router-dom"
import {
  GetUserapiResultsGetSavepointByRunIdApiResponse,
  useGetUserapiAdminTestsGetTestByIdByTestIdQuery,
  useGetUserapiResultsGetSavepointByRunIdByRunIdQuery,
  usePostUserapiAdminTestsAssignTestMutation,
  usePostUserapiResultsSavepointByRunIdMutation,
  usePutUserapiResultsFinalResultsByRunIdMutation,
  usePostUserapiAdminTestsGetRunIdMutation,
} from "../app/services/meseeknaApi"
import { RootState, useTypedDispatch, useTypedSelector } from "../app/store"
import HeaderSection from "../components/header-section"
import MessagesWithSearch from "../components/messages-with-search"
import SidebarMinimal from "../features/sidebar/components/sidebar-minimal"
import SimulationHeader from "../components/simulation-header"
import DecisionPlanSection from "../features/simulation-decisions/components/decision-plan-section"
import ModalBreaktime from "../features/simulation/components/modal-breaktime"
import {
  setSecondsElapsed,
  simulationSlice,
} from "../features/simulation/simulationSlice"
import {
  createTimer,
  destroyTimer,
  resumeTimer,
  resetSimulationSafe,
} from "../features/simulation/simulationThunks"
import { SavePointData } from "../features/simulation/types"
import {
  convertToSeconds,
  resetSimulationState,
  urlHasResetTrigger,
} from "../features/simulation/utils"
import { resetDecisions } from "../features/simulation-decisions/decisionsSlice"
import { SAVEPOINT_TIMER } from "../utils/constants"
import NotificationContainer from "../features/auth/components/Notifications/NotificationContainer"
import { useRoleRedirect } from "../features/auth/hooks/useRoleRedirect"
import SimulationConnectionIssueModal from "../components/SimulationConnectionIssueModal"
import FinishedModal from "../components/finished-modal"
import ErrorBoundary from "../components/ErrorBoundary"

const Simulation: FunctionComponent = () => {
  const dispatch = useTypedDispatch()
  const navigate = useNavigate()
  const { testId: testIdParam, runId: runIdParam } = useParams()
  const [runId, setRunId] = useState(runIdParam)
  const { getSimulationStatus } = useRoleRedirect()
  const [postAssignTest] = usePostUserapiAdminTestsAssignTestMutation()
  const [getRunId] = usePostUserapiAdminTestsGetRunIdMutation()

  const [isModalBreaktimeOpen, setModalBreaktimeOpen] = useState(false)

  const { duration, timer, secondsElapsed, messages } = useTypedSelector(
    (state: RootState) => {
      return state.simulationSlice
    },
  )
  const { organization, user } = useTypedSelector((state: RootState) => {
    return state.authSlice
  })

  // Add a flag to track if the initial restore from savepoint is complete
  const [hasRestoredFromSavepoint, setHasRestoredFromSavepoint] =
    useState(false)

  // Add a single isHydrated state
  const [isHydrated, setIsHydrated] = useState(false)
  // Ref to skip the first effect run after hydration
  const hasSkippedFirstEffect = useRef(false)
  // Track if user has started interacting - timer should not start until first user action
  const [userHasStarted, setUserHasStarted] = useState(false)

  // Log runId and skip value for the GET savepoint hook
  const skipSavepointGet = !runId || isNaN(Number(runId)) || Number(runId) <= 0
  const {
    data: getSavePointAPIData,
    error: getSavePointApiError,
    refetch,
  } = useGetUserapiResultsGetSavepointByRunIdByRunIdQuery<GetUserapiResultsGetSavepointByRunIdApiResponse>(
    { runId: Number(runId) },
    {
      skip: skipSavepointGet,
      refetchOnMountOrArgChange: true,
    },
  )

  const initialFinalData: SavePointData = {
    id: 0,
    startDate: "",
    endDate: "",
    length: "",
    time: "",
    name: "",
    log: [],
  }
  const [finalData, setFinalData] = useState(initialFinalData)

  const [postSavepointByRunId] = usePostUserapiResultsSavepointByRunIdMutation()

  const [postFinalResults] = usePutUserapiResultsFinalResultsByRunIdMutation()

  const [isConnectionIssue, setIsConnectionIssue] = useState(false)
  const [retryStatus, setRetryStatus] = useState("")
  const [retryCount, setRetryCount] = useState(0)
  const [retryDelay, setRetryDelay] = useState(2000) // start with 2 seconds
  const [retryTimeoutId, setRetryTimeoutId] = useState<NodeJS.Timeout | null>(
    null,
  )
  const [retryIntervalId, setRetryIntervalId] = useState<NodeJS.Timeout | null>(
    null,
  )

  // State for finished modal and saving final results
  const [showFinishedModal, setShowFinishedModal] = useState(false)
  const [isSavingFinal, setIsSavingFinal] = useState(false)
  const [saveFinalError, setSaveFinalError] = useState<string | null>(null)

  // Consolidated assignment and validation logic
  const validateAndAssignSimulation = async () => {
    if (!testIdParam || !organization?.id || !user?.id) return
    const testIdFromUrl = Number(testIdParam)
    const runIdFromUrl = runIdParam ? Number(runIdParam) : undefined
    try {
      // If runId is present in the URL, validate it
      if (runIdFromUrl) {
        // Fetch assignment by runId (using getRunId for this runId)
        const assignmentResp = await getRunId({
          testId: testIdFromUrl,
          organizationId: Number(organization.id),
          userId: user.id,
        }).unwrap()
        let assignment: Record<string, unknown> | null = null
        if (
          Array.isArray(assignmentResp.data) &&
          assignmentResp.data.length > 0
        ) {
          assignment = assignmentResp.data[0]
        } else if (
          assignmentResp.data &&
          typeof assignmentResp.data === "object"
        ) {
          assignment = assignmentResp.data
        }
        if (!assignment) {
          // Invalid runId or assignment, redirect
          navigate("/dashboard")
          return
        }
        if (Number(assignment.testId) !== testIdFromUrl) {
          // Mismatched testId, redirect
          navigate("/dashboard")
          return
        }
        if (assignment.dateCompleted) {
          // Completed, redirect
          navigate(`/simulations/complete/${testIdFromUrl}/${runIdFromUrl}`)
          return
        }
        // Assignment is valid and not completed, set runId and allow simulation to load
        setRunId(String(runIdFromUrl))
        return
      }
      // No runId in URL: check assignment/completion as before
      const response = await getRunId({
        testId: testIdFromUrl,
        organizationId: Number(organization.id),
        userId: user.id,
      }).unwrap()
      let dateCompleted: string | undefined
      let runIdFromApi: string | number | undefined
      if (Array.isArray(response.data) && response.data.length > 0) {
        dateCompleted = response.data[0].dateCompleted
        runIdFromApi = response.data[0].runId || response.data[0].id
      } else if (response.data && typeof response.data === "object") {
        dateCompleted = (response.data as { dateCompleted?: string })
          .dateCompleted
        runIdFromApi =
          (response.data as { runId?: string | number; id?: string | number })
            .runId || (response.data as { id?: string | number }).id
      }
      if (dateCompleted) {
        navigate(`/simulations/complete/${testIdFromUrl}/${runIdFromApi}`)
        return
      }
      if (runIdFromApi) {
        setRunId(String(runIdFromApi))
        navigate(`/simulations/${testIdFromUrl}/${runIdFromApi}`, {
          replace: true,
        })
        return
      }
      // Only assign if no runId and no dateCompleted
      const assignResult = await postAssignTest({
        body: {
          testId: testIdFromUrl,
          organizationId: Number(organization.id),
          userId: user.id,
        },
      })
      if (!("error" in assignResult) && assignResult.data?.data?.[0]?.id) {
        const newRunId = String(assignResult.data.data[0].id)
        setRunId(newRunId)
        navigate(`/simulations/${testIdFromUrl}/${newRunId}`, { replace: true })
        return
      } else {
        console.error("Error or no runId in assignment response:", assignResult)
        return
      }
    } catch (e) {
      console.error("Error in assignment/completion/validation check:", e)
    }
  }

  useEffect(() => {
    validateAndAssignSimulation()
  }, [testIdParam, runIdParam, organization?.id, user?.id])

  // Hydration logic: hydrate from savepoint if exists, otherwise start fresh
  useEffect(() => {
    if (!runId) return
    // Handle both array and object responses for savepoint data
    const data =
      getSavePointAPIData && "data" in getSavePointAPIData
        ? getSavePointAPIData.data
        : undefined
    if (
      data &&
      ((Array.isArray(data) && data.length > 0) ||
        (!Array.isArray(data) && typeof data === "object"))
    ) {
      // Hydrate from savepoint
      processSavePointData()
    } else if (getSavePointApiError) {
      // No savepoint found (first time), start fresh
      setHasRestoredFromSavepoint(true)
      setIsHydrated(true)
      hasSkippedFirstEffect.current = false
      dispatch(resetSimulationSafe())
      // Also set up the simulation scenario/messages if simResponse is available
      if (simResponse && simResponse.data) {
        dispatch(simulationSlice.actions.setupSimulation(simResponse))
      }
      console.log("[Simulation] No savepoint found, starting fresh")
    }
  }, [runId, getSavePointAPIData, getSavePointApiError])

  // Process savepoint data when it's available
  const processSavePointData = async () => {
    if (
      getSavePointAPIData &&
      "code" in getSavePointAPIData &&
      getSavePointAPIData?.code == 200 &&
      "data" in getSavePointAPIData &&
      getSavePointAPIData.data
    ) {
      const savepointObj = getSavePointAPIData.data
      if (
        typeof savepointObj === "object" &&
        savepointObj !== null &&
        "data" in savepointObj &&
        "elapsedTime" in savepointObj
      ) {
        let resData
        if (typeof savepointObj.data === "string") {
          resData = JSON.parse(savepointObj.data)
        } else {
          resData = savepointObj.data
        }

        const { elapsedTime } = savepointObj
        let time = 0
        if (elapsedTime !== null) {
          time = moment(elapsedTime, "HH:mm:ss").diff(
            moment().startOf("day"),
            "seconds",
          )
        }
        dispatch(setSecondsElapsed(time))
        let logData = []
        if (
          typeof resData === "object" &&
          resData !== null &&
          "log" in resData
        ) {
          logData = resData.log ?? []
          dispatch(simulationSlice.actions.setMessages(logData))
          dispatch(simulationSlice.actions.setPlans(logData))
        }
      }
    }
    if (getSavePointApiError) {
      console.error("Error data:", getSavePointApiError)
    }
    setHasRestoredFromSavepoint(true)
    setIsHydrated(true)
    hasSkippedFirstEffect.current = false
    console.log("[Simulation] hasRestoredFromSavepoint set to true")
  }

  const closeModalBreaktime = useCallback(() => {
    dispatch(resumeTimer())
    setModalBreaktimeOpen(false)
  }, [])

  const testId = parseInt(testIdParam as string)

  const {
    data: simResponse,
    isSuccess: simLoadIsSuccess,
    isFetching: simLoadIsFetching,
  } = useGetUserapiAdminTestsGetTestByIdByTestIdQuery({
    testId: testId,
  })

  useEffect(() => {
    console.log("Component mounted or testId/runId changed:", testId, runId)

    // CRITICAL: Clear any persisted state synchronously to prevent tutorial state leakage.
    // This must happen BEFORE any async operations. The savepoint API is the source of truth.
    localStorage.removeItem("persist:msk:simulation")
    dispatch(resetSimulationSafe())
    dispatch(resetDecisions())

    // If we don't have a runId yet and we're on a path with both testId and an undefined runId
    // try to get an existing assignment
    if (!runId && testIdParam && !runIdParam) {
      console.log("Missing runId, checking for assignment")
      validateAndAssignSimulation()
    }
    // Only refetch if we have a valid runId
    else if (runId) {
      console.log("Have runId, fetching savepoint data:", runId)
      refetch() // This is to fetch the latest savepoint data
    }

    if (urlHasResetTrigger()) {
      resetSimulationState(dispatch)
    }

    // Check if the simulation is completed, redirect to /simulations/complete if so
    const checkAndRedirect = async () => {
      const simStatus = await getSimulationStatus(
        testId,
        Number(organization.id),
        user.id,
      )
      if (simStatus.status === "completed") {
        navigate(`/simulations/complete/${testId}/${runId}`)
      }
    }
    checkAndRedirect()

    return () => {
      // Function to execute when component unmounts
      resetSimulationState(dispatch, false)
    }
  }, [testId, runId]) // Add dependencies for testId and runId

  useEffect(() => {
    console.debug("Sim load success?", simLoadIsSuccess, getSavePointAPIData)
    if (!simLoadIsSuccess) return
    dispatch(simulationSlice.actions.setupSimulation(simResponse))

    // Process savepoint data if available
    if (getSavePointAPIData && runId) {
      processSavePointData()
    }

    console.debug("Setting final data")
    setFinalData((prevFinalData) => ({
      ...prevFinalData,
      id:
        simResponse.data && simResponse?.data[0].id != undefined
          ? simResponse?.data[0]?.id
          : 0,
      name:
        simResponse.data && simResponse?.data[0].title != undefined
          ? simResponse?.data[0]?.title
          : "",
      startDate: moment(new Date()).utc().format("YYYY-MM-DD hh:mm:ss"),
      time: moment("2024-01-01")
        .seconds(secondsElapsed ?? 0)
        .format("HH:mm:ss"),
    }))
  }, [
    simLoadIsFetching,
    simLoadIsSuccess,
    simResponse,
    dispatch,
    getSavePointAPIData,
    runId,
  ])

  useEffect(() => {
    if (!simLoadIsSuccess) return
    if (!userHasStarted) {
      console.debug("simulation loaded but waiting for user to start")
      return
    }
    // Only create timer when simulation loads AND user has started interacting
    // The createTimer thunk handles checking for existing timers internally
    console.debug("creating timer - user has started simulation")
    dispatch(createTimer())
    return () => {
      dispatch(destroyTimer())
    }
    // NOTE: Removed simLoadIsFetching from deps - it was causing unnecessary timer
    // destroy/recreate cycles during fetch state changes, making the timer run fast
  }, [simLoadIsSuccess, userHasStarted])

  // Monitor simulation time, end sim if time is up
  useEffect(() => {
    if (secondsElapsed === 0) return
    if (!simLoadIsSuccess) return
    if (!timer) return
    const durationSeconds = convertToSeconds(duration)
    if (durationSeconds === 0) return
    if (process.env.REACT_APP_DEMO_MODE?.toLowerCase() == "true") {
      if (process.env.NODE_ENV === "production" && secondsElapsed > 60) {
        // Show an alert to the user with a message and then redirect to /simulations/complete
        navigate(`/simulations/complete/${testId}/${runId}`)
        return
      }
    }
    if (secondsElapsed > 0 && secondsElapsed % SAVEPOINT_TIMER === 0) {
      saveDataPointDetails()
    }
    if (secondsElapsed >= durationSeconds) {
      setShowFinishedModal(true)
      console.log("[Simulation] secondsElapsed >= durationSeconds", {
        secondsElapsed,
        durationSeconds,
        isSavingFinal,
        saveFinalError,
      })
      if (!isSavingFinal && !saveFinalError) {
        console.log("[Simulation] Calling saveFinalDataPoint", {
          isSavingFinal,
          saveFinalError,
        })
        saveFinalDataPoint()
      }
      console.debug("simulation finished")
    }
  }, [secondsElapsed])

  // Log state changes for isSavingFinal and saveFinalError
  useEffect(() => {
    console.log("[Simulation] isSavingFinal changed:", isSavingFinal)
  }, [isSavingFinal])
  useEffect(() => {
    console.log("[Simulation] saveFinalError changed:", saveFinalError)
  }, [saveFinalError])

  useEffect(() => {
    // Only run after hydration
    if (!isHydrated) return
    if (!hasSkippedFirstEffect.current) {
      hasSkippedFirstEffect.current = true
      console.log("[Simulation] Skipping first save after hydration")
      return
    }
    console.log(
      "[Simulation] saveDataPointDetails called from [messages] useEffect",
    )
    saveDataPointDetails()
  }, [messages, isHydrated])

  const retrySaveDataPoint = (
    nextRetryCount: number,
    nextRetryDelay: number,
  ) => {
    setRetryCount(nextRetryCount)
    setRetryDelay(nextRetryDelay)
    let secondsLeft = Math.round(nextRetryDelay / 1000)
    setRetryStatus(
      `Retrying in ${secondsLeft} seconds... (Attempt ${nextRetryCount})`,
    )
    // Clear any previous interval
    if (retryIntervalId) {
      clearInterval(retryIntervalId)
      setRetryIntervalId(null)
    }
    const intervalId = setInterval(() => {
      secondsLeft -= 1
      if (secondsLeft <= 0) {
        clearInterval(intervalId)
        setRetryIntervalId(null)
        setRetryStatus(`Retrying now... (Attempt ${nextRetryCount})`)
        saveDataPointDetails(nextRetryCount, nextRetryDelay)
      } else {
        setRetryStatus(
          `Retrying in ${secondsLeft} seconds... (Attempt ${nextRetryCount})`,
        )
      }
    }, 1000)
    setRetryIntervalId(intervalId)
  }

  const saveDataPointDetails = async (
    currentRetryCount = retryCount,
    currentRetryDelay = retryDelay,
  ) => {
    finalData.length = duration
    finalData.time = moment("2024-01-01")
      .seconds(secondsElapsed)
      .format("HH:mm:ss")
    finalData.log = [...messages]
    const time = moment("2024-01-01").seconds(secondsElapsed).format("HH:mm:ss")
    const payload = {
      runId: Number(runId),
      datetime: moment(new Date()).utc().format("YYYY-MM-DD hh:mm:ss"),
      runTimer: time,
      data: finalData,
    }
    try {
      const result = await postSavepointByRunId(payload)
      if (result && "error" in result) {
        throw result.error
      }
      // Always clear modal and retry state on success
      setIsConnectionIssue(false)
      setRetryStatus("")
      setRetryCount(0)
      setRetryDelay(2000)
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId)
        setRetryTimeoutId(null)
      }
      if (retryIntervalId) {
        clearInterval(retryIntervalId)
        setRetryIntervalId(null)
      }
      dispatch(resumeTimer())
    } catch (error) {
      setIsConnectionIssue(true)
      dispatch(destroyTimer())
      const nextRetryCount = currentRetryCount + 1
      const nextRetryDelay = Math.min(currentRetryDelay * 2, 60000)
      retrySaveDataPoint(nextRetryCount, nextRetryDelay)
    }
  }

  // Handler for manual reconnect
  const handleReconnect = () => {
    if (retryTimeoutId) {
      clearTimeout(retryTimeoutId)
      setRetryTimeoutId(null)
    }
    if (retryIntervalId) {
      clearInterval(retryIntervalId)
      setRetryIntervalId(null)
    }
    setRetryStatus("Reconnecting now...")
    setRetryDelay(2000)
    setRetryCount(0)
    saveDataPointDetails(0, 2000)
  }

  // Save final data point with saving state and error handling
  const saveFinalDataPoint = async () => {
    console.log("[Simulation] saveFinalDataPoint called")
    setIsSavingFinal(true)
    setSaveFinalError(null)
    const final = { ...finalData }
    final.endDate = moment(new Date()).utc().format("YYYY-MM-DD hh:mm:ss")
    final.log = [...messages]
    final.time = ""
    setFinalData({ ...final })
    const payload = {
      runId: Number(runId),
      body: final,
    }
    try {
      console.log("[Simulation] Posting final results", payload)
      await postFinalResults(payload)
      setIsSavingFinal(false)
      setSaveFinalError(null)
      dispatch(destroyTimer())
      const noReloadFlag = false
      resetSimulationState(dispatch, noReloadFlag)
      // Don't navigate away, just allow Done button to be enabled
      console.log("[Simulation] Final results saved successfully")
    } catch (error: unknown) {
      setIsSavingFinal(false)
      let message = "There was a problem saving your results. Please try again."
      if (error instanceof Error) {
        message = error.message
      }
      setSaveFinalError(message)
      console.error("[Simulation] Mutation error", error)
    }
  }

  // Retry handler for FinishedModal
  const handleRetryFinalSave = () => {
    saveFinalDataPoint()
  }

  // Move sim status check to run after restore/messages update
  useEffect(() => {
    if (!hasRestoredFromSavepoint) return
    const checkAndRedirect = async () => {
      const simStatus = await getSimulationStatus(
        testId,
        Number(organization.id),
        user.id,
      )
      if (simStatus.status === "completed") {
        navigate(`/simulations/complete/${testId}/${runId}`)
      }
    }
    checkAndRedirect()
  }, [hasRestoredFromSavepoint, messages])

  // Handler to start the simulation - resets timer and begins
  const handleStartSimulation = useCallback(() => {
    if (userHasStarted) return
    if (!hasRestoredFromSavepoint) {
      console.debug("User starting simulation - resetting timer to 0:00")
      dispatch(setSecondsElapsed(0))
    } else {
      console.debug("User resuming simulation - keeping restored elapsed time")
    }
    setUserHasStarted(true)
  }, [userHasStarted, hasRestoredFromSavepoint, dispatch])

  return (
    <div className="relative bg-base-white w-full h-[100dvh] overflow-hidden flex flex-row items-start justify-start font-text-md-regular">
      <SidebarMinimal />
      <div className="w-full h-[100dvh] flex-1 flex flex-col">
        <NotificationContainer errorMessage={""} successMessage={""} />
        <HeaderSection hideBreadcrumbs hideHelpIcon hideProfileIcon />
        <div
          id="simulationContainer"
          className="relative overflow-hidden box-border w-full flex-1 flex flex-col justify-start py-6 px-9 gap-[19px] text-left text-sm text-gray-600 font-text-sm-medium border-r-[1px] border-solid border-gray-200">
          <SimulationHeader
            setModalBreaktimeOpen={setModalBreaktimeOpen}
            simulation={simResponse?.data ? simResponse?.data : []}
          />
          <div className="w-full overflow-hidden flex-1 flex flex-col items-start justify-start py-0 px-0 box-border gap-[12px] text-base text-gray-700">
            <DndContext
              autoScroll={false}
              collisionDetection={rectIntersection}
              modifiers={[restrictToWindowEdges]}>
              <div
                id="boxesContainer"
                className="self-stretch overflow-hidden flex-1 flex flex-row items-stretch gap-[19px] text-sm text-gray-500">
                <MessagesWithSearch />
                <ErrorBoundary name="DecisionPlanSection">
                  <DecisionPlanSection />
                </ErrorBoundary>
              </div>
            </DndContext>
          </div>
          {/* Start simulation overlay - shows until user clicks to begin */}
          {/* Gate on hasRestoredFromSavepoint so the overlay doesn't appear before
              savepoint data loads (e.g. when sim data is cached from tutorial pages) */}
          {simLoadIsSuccess && hasRestoredFromSavepoint && !userHasStarted && (
            <div
              className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 cursor-pointer"
              onClick={handleStartSimulation}
            >
              <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md text-center">
                <div className="mb-4">
                  <svg className="w-16 h-16 mx-auto text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Ready to Begin?</h2>
                <p className="text-gray-600 mb-6">Your simulation timer will start when you click the button below.</p>
                <button
                  className="bg-brand-blue hover:bg-brand-blue-hover text-white font-semibold py-3 px-8 rounded-lg transition-colors text-lg"
                  onClick={handleStartSimulation}
                >
                  Start Simulation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {isModalBreaktimeOpen && (
        <ModalBreaktime closeModalBreaktime={closeModalBreaktime} />
      )}
      <SimulationConnectionIssueModal
        isOpen={isConnectionIssue}
        retryStatus={retryStatus}
        onReconnect={handleReconnect}
      />
      {showFinishedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <FinishedModal
            isSaving={isSavingFinal}
            saveError={saveFinalError}
            onRetry={handleRetryFinalSave}
          />
        </div>
      )}
    </div>
  )
}

export default Simulation
